<template>
  <div ref="wrapperEl" class="scroll-marquee">
    <div class="scroll-marquee__content" :style="boxStyles">
      <span
          v-for="index in Array.from({length: state.cloneCount})"
          ref="cloneEls"
          :key="`item-${index}`"
      >
        <span
            class="scroll-marquee__inner"
            :class="{ 'scroll-marquee__inner--initialized': state.initialized }"
        >
          <slot/>
        </span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, onMounted, reactive, ref, watch} from 'vue'
import {useWindowSize} from '#imports'
import {useWindowScroll} from '~/composables/useWindowScroll'
import {useBreakpointsStore} from '~/store/breakpoints'

const wrapperEl = ref<HTMLElement>()
const cloneEls = ref<HTMLElement>()

const breakpointsStore = useBreakpointsStore()

// make marquee scroll faster on mobile so you can read more of the words
const breakpointSpeeds = {
  xs: 0.7,
  sm: 0.7,
  md: 0.7,
  lg: 0.6,
  xl: 0.5,
  xxl: 0.4
}

const props = defineProps({
  reverse: {type: Boolean, default: false, required: false}
})

const state = reactive({
  cloneCount: 2,
  movingDistance: 0,
  initialized: false
})

onMounted(() => {
  // watch for scroll
  const scrollState = useWindowScroll()
  watch(scrollState, (newState) => {
    if (state.initialized) {
      updateMarquee(newState.scroll)
    }
  })

  // watch for resize
  const windowState = useWindowSize()
  watch(windowState, () => {
    if (state.initialized) {
      initMarquee()
      updateMarquee(scrollState.scroll)
    }
  })

  nextTick(() => {
    initMarquee()
    updateMarquee(scrollState.scroll)
    state.initialized = true
  })
})

const initMarquee = () => {
  if (cloneEls.value[0]) {
    const childWidth = (cloneEls.value[0]) ? cloneEls.value[0].offsetWidth : 0
    if (childWidth !== 0) {
      state.cloneCount = Math.ceil(window.innerWidth / childWidth) + 1
    } else {
      if (state.cloneCount !== 2) {
        state.cloneCount = 2
      }
      // if for some reason the dom wasn't ready, we'll try again in 125ms
      window.setTimeout(() => {
        initMarquee()
      }, 125)
    }
  }
}

const updateMarquee = (scrollY) => {
  // adjust speed based on breakpoint
  let speed = scrollY * breakpointSpeeds[breakpointsStore.getCurrentBreakpoint.name]

  const childWidth = (cloneEls.value[0]) ? cloneEls.value[0].offsetWidth : 0

  // offset for reverse, so it always looks good (even at scroll 0)
  if (props.reverse) {
    speed -= (childWidth / 2)
  }

  if (!props.reverse) {
    const movingDistance = speed - (Math.floor(speed / childWidth) * childWidth)
    state.movingDistance = (movingDistance > 0) ? movingDistance : 0
  } else {
    const movingDistance = speed - (Math.ceil(speed / childWidth) * childWidth)
    state.movingDistance = (movingDistance < 0) ? movingDistance : 0
  }
}

const boxStyles = computed(() => ({
  transform: `translateX(${(!props.reverse) ? -state.movingDistance : state.movingDistance}px)`
}))

</script>

<style scoped lang="postcss">
.scroll-marquee {
  pointer-events: none;
  position: relative;
  overflow: hidden;

  &__content {
    display: inline-block;
    user-select: none;
    white-space: nowrap;
  }

  &__inner {
    display: inline-block;
    opacity: 0;
    transition-property: opacity;
    transition-duration: 350ms;
    transition-timing-function: ease;
    padding-top: 1px;
    /* fixes overflow issue */
    &--initialized {
      opacity: 1;
    }
  }
}
</style>
